import { useState } from "react";
import { Volume2, VolumeX, Music, Music2, Mic } from "lucide-react";
import { useSfx } from "@/hooks/useSfx";
import { useMusic } from "@/hooks/useMusic";
import { useInstructorVoice } from "@/hooks/useInstructorVoice";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";

/**
 * Floating audio quick-control. Visible only when SFX scope is active
 * (beta/admin on a beta surface). Exposes both Music and SFX controls.
 */
export default function FloatingAudioToggle() {
  const sfx = useSfx();
  const music = useMusic();
  const voice = useInstructorVoice();
  const [open, setOpen] = useState(false);

  if (!sfx.inScope) return null;

  const anyOn = !sfx.muted || music.enabled || voice.enabled;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Audio settings"
          aria-expanded={open}
          title="Audio settings"
          className={`group fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 rounded-full border backdrop-blur-2xl px-3.5 py-2 text-[11px] font-heading font-semibold uppercase tracking-[0.16em] transition-all duration-300 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/60 focus-visible:ring-offset-0 ${
            open
              ? "border-cyan-300/70 bg-gradient-to-br from-cyan-400/20 via-slate-900/55 to-violet-500/20 text-cyan-100 shadow-[0_0_38px_-6px_hsl(190_90%_60%/0.65)]"
              : "border-cyan-300/35 bg-gradient-to-br from-cyan-400/10 via-slate-900/55 to-violet-500/10 text-cyan-100/85 shadow-[0_14px_40px_-14px_hsl(190_90%_60%/0.45)] hover:border-cyan-200/60 hover:text-cyan-50 hover:from-cyan-400/16 hover:to-violet-500/16 hover:shadow-[0_0_42px_-6px_hsl(190_90%_60%/0.55)]"
          }`}
        >
          <span aria-hidden className="pointer-events-none absolute inset-x-2 top-0 h-px bg-gradient-to-r from-transparent via-cyan-200/60 to-transparent" />
          <span aria-hidden className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-inset ring-white/10" />
          <span className="relative inline-flex h-5 w-5 items-center justify-center rounded-full border border-cyan-300/40 bg-cyan-400/10">
            {anyOn ? <Volume2 className="h-3 w-3" /> : <VolumeX className="h-3 w-3" />}
          </span>
          <span className="relative [text-shadow:_0_1px_8px_rgba(0,0,0,0.55)]">Audio</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={12}
        className="relative w-72 overflow-hidden rounded-2xl border border-cyan-300/35 bg-gradient-to-br from-cyan-400/10 via-slate-950/85 to-violet-500/10 p-0 backdrop-blur-2xl shadow-[0_24px_60px_-18px_hsl(190_90%_60%/0.5)] origin-bottom-right will-change-transform data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:duration-300 data-[state=closed]:duration-200 data-[state=open]:ease-[cubic-bezier(0.22,1,0.36,1)] data-[state=closed]:ease-[cubic-bezier(0.4,0,0.2,1)] data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-[0.98] data-[state=closed]:zoom-out-[0.98] data-[state=open]:slide-in-from-bottom-2 data-[state=closed]:slide-out-to-bottom-1"
      >
        <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-200/60 to-transparent" />
        <span aria-hidden className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/10" />
        <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-2/3 bg-gradient-to-b from-white/[0.06] to-transparent" />
        <span aria-hidden className="pointer-events-none absolute -inset-px rounded-2xl [background:radial-gradient(120%_60%_at_50%_-10%,hsl(190_90%_60%/0.18),transparent_60%)]" />

        <div className="relative px-4 pt-4 pb-1.5">
          <div className="text-[10px] font-heading font-semibold uppercase tracking-[0.22em] text-cyan-200/80">
            Audio Console
          </div>
        </div>

        <div className="relative divide-y divide-white/5 px-4 pb-4">
          {/* Music row */}
          <div className="space-y-2.5 py-3">
            <div className="flex items-center justify-between gap-3">
              <label htmlFor="music-toggle" className="flex items-center gap-2 text-[13px] font-heading font-semibold text-foreground/95">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-cyan-300/35 bg-cyan-400/10 text-cyan-200">
                  <Music className="h-3.5 w-3.5" />
                </span>
                Music
              </label>
              <Switch id="music-toggle" checked={music.enabled} onCheckedChange={music.setEnabled} />
            </div>
            <Slider
              aria-label="Music volume"
              value={[Math.round(music.volume * 100)]}
              min={0} max={100} step={5}
              disabled={!music.enabled}
              onValueChange={([v]) => music.setVolume((v ?? 0) / 100)}
            />
          </div>

          {/* SFX row */}
          <div className="py-3">
            <div className="flex items-center justify-between gap-3">
              <label htmlFor="sfx-toggle" className="flex items-center gap-2 text-[13px] font-heading font-semibold text-foreground/95">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-cyan-300/35 bg-cyan-400/10 text-cyan-200">
                  <Music2 className="h-3.5 w-3.5" />
                </span>
                Sound effects
              </label>
              <Switch id="sfx-toggle" checked={!sfx.muted} onCheckedChange={(v) => sfx.setMuted(!v)} />
            </div>
          </div>

          {/* Voice row */}
          <div className="space-y-2.5 py-3">
            <div className="flex items-center justify-between gap-3">
              <label htmlFor="voice-toggle" className="flex items-center gap-2 text-[13px] font-heading font-semibold text-foreground/95">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-cyan-300/35 bg-cyan-400/10 text-cyan-200">
                  <Mic className="h-3.5 w-3.5" />
                </span>
                Instructor voice
              </label>
              <Switch id="voice-toggle" checked={voice.enabled} onCheckedChange={voice.setEnabled} />
            </div>
            <Slider
              aria-label="Instructor voice volume"
              value={[Math.round(voice.volume * 100)]}
              min={0} max={100} step={5}
              disabled={!voice.enabled}
              onValueChange={([v]) => voice.setVolume((v ?? 0) / 100)}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}